// src/components/Operations/Fleet/VehicleDetail.jsx
import React, { useEffect, useState, useCallback } from "react";
import useFleetApi from "./useFleetApi";
import InsuranceFormModal from "./InsuranceFormModal";
import InspectionModal from "./InspectionModal";
import "./FleetReports.css";

const MAINT_KINDS = [
  ["oil_change", "Oil Change"],
  ["tire_rotation", "Tire Rotation"],
  ["brake_inspection", "Brake Inspection"],
  ["general_service", "General Service"],
  ["regulatory_inspection", "Regulatory Inspection"],
];
const today = () => new Date().toISOString().slice(0, 10);

export default function VehicleDetail({ vehicle }) {
  const {
    listRecords, createRecord, completeAndRoll,
    listFuelLogs, createFuelLog,
    listInsurance, createInsurance,
    listVehicleDocs, uploadVehicleDoc,
    listInspections, submitInspection,
  } = useFleetApi();

  const [records, setRecords] = useState([]);
  const [fuelLogs, setFuelLogs] = useState([]);
  const [insurance, setInsurance] = useState([]);
  const [docs, setDocs] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInsuranceForm, setShowInsuranceForm] = useState(false);
  const [showInspect, setShowInspect] = useState(false);
  const [maintForm, setMaintForm] = useState(null); // null=closed
  const [fuelForm, setFuelForm] = useState(null);
  const vehicleId = vehicle?.id;

  const loadAll = useCallback(async () => {
    if (!vehicleId) return;
    setLoading(true);
    try {
      const [r, f, i, d, insp] = await Promise.all([
        listRecords(vehicleId), listFuelLogs(vehicleId), listInsurance(vehicleId),
        listVehicleDocs(vehicleId).catch(() => []),
        listInspections(vehicleId).catch(() => []),
      ]);
      const arr = (x) => (Array.isArray(x) ? x : x?.results || []);
      setRecords(arr(r)); setFuelLogs(arr(f)); setInsurance(arr(i));
      setDocs(arr(d)); setInspections(arr(insp));
    } catch (err) {
      console.error("Failed to load vehicle detail data", err);
    } finally {
      setLoading(false);
    }
  }, [vehicleId, listRecords, listFuelLogs, listInsurance, listVehicleDocs, listInspections]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const markAsComplete = async (rec) => {
    try {
      await completeAndRoll({
        vehicle: vehicleId, kind: rec.kind, service_date: today(),
        odometer_at_service: vehicle.odometer || 0, vendor: rec.vendor || "Internal",
        cost: rec.cost || 0, notes: rec.notes || "Completed",
      });
      await loadAll();
    } catch (err) { console.error("Failed to complete record", err); }
  };

  const submitMaint = async (e) => {
    e.preventDefault();
    await createRecord({
      vehicle: vehicleId,
      kind: maintForm.kind,
      service_date: maintForm.service_date || today(),
      odometer_at_service: maintForm.odometer ? parseInt(maintForm.odometer, 10) : (vehicle.odometer || 0),
      vendor: maintForm.vendor || "",
      cost: maintForm.cost ? parseFloat(maintForm.cost) : 0,
      notes: maintForm.notes || "",
    });
    setMaintForm(null);
    await loadAll();
  };

  const submitFuel = async (e) => {
    e.preventDefault();
    await createFuelLog({
      vehicle: vehicleId,
      date: fuelForm.date || today(),
      volume: fuelForm.volume ? parseFloat(fuelForm.volume) : 0,
      price_total: fuelForm.price_total ? parseFloat(fuelForm.price_total) : 0,
      odometer: fuelForm.odometer ? parseInt(fuelForm.odometer, 10) : null,
    });
    setFuelForm(null);
    await loadAll();
  };

  const onUploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { await uploadVehicleDoc(vehicleId, file); await loadAll(); }
    catch (err) { console.error("Doc upload failed", err); }
    e.target.value = "";
  };

  if (loading) return <div className="fleet-skel">Loading details…</div>;

  return (
    <div className="fleet-reports vdetail">
      {/* Vehicle Info */}
      <section className="panel">
        <div className="panel-head flex-between">
          <h3>Vehicle Information</h3>
          <button className="mini" onClick={() => setShowInspect(true)}>Start Inspection</button>
        </div>
        <div className="panel-body grid grid--2 small">
          <div><strong>Make:</strong> {vehicle.make || "—"}</div>
          <div><strong>Model:</strong> {vehicle.model || "—"}</div>
          <div><strong>Year:</strong> {vehicle.year || "—"}</div>
          <div><strong>VIN:</strong> <span className="mono">{vehicle.vin || "—"}</span></div>
          <div><strong>Plate:</strong> {vehicle.plate || "—"}</div>
          <div><strong>Status:</strong> <span className="cap">{vehicle.status || "—"}</span></div>
          <div><strong>Odometer:</strong> {vehicle.odometer?.toLocaleString?.() || "—"} mi</div>
          <div><strong>Type:</strong> <span className="cap">{vehicle.type || "—"}</span></div>
        </div>
      </section>

      {/* Maintenance */}
      <section className="panel">
        <div className="panel-head flex-between">
          <h3>Maintenance Records</h3>
          <button className="mini" onClick={() => setMaintForm({ kind: "oil_change", service_date: today() })}>+ Log Maintenance</button>
        </div>
        {maintForm && (
          <form className="fleet-inline-form" onSubmit={submitMaint}>
            <select value={maintForm.kind} onChange={(e) => setMaintForm({ ...maintForm, kind: e.target.value })}>
              {MAINT_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <input type="date" value={maintForm.service_date} onChange={(e) => setMaintForm({ ...maintForm, service_date: e.target.value })} />
            <input placeholder="Vendor" value={maintForm.vendor || ""} onChange={(e) => setMaintForm({ ...maintForm, vendor: e.target.value })} />
            <input type="number" step="0.01" placeholder="Cost $" value={maintForm.cost || ""} onChange={(e) => setMaintForm({ ...maintForm, cost: e.target.value })} />
            <input type="number" placeholder="Odometer" value={maintForm.odometer || ""} onChange={(e) => setMaintForm({ ...maintForm, odometer: e.target.value })} />
            <button type="submit">Save</button>
            <button type="button" className="secondary" onClick={() => setMaintForm(null)}>Cancel</button>
          </form>
        )}
        <div className="table">
          <div className="thead"><div>Kind</div><div>Date</div><div>Vendor</div><div>Cost</div><div>Status</div><div></div></div>
          <div className="tbody">
            {records.length > 0 ? records.map((r) => (
              <div key={r.id} className="row small">
                <div className="cap">{(r.kind || "—").replace(/_/g, " ")}</div>
                <div>{r.service_date || "—"}</div>
                <div>{r.vendor || "—"}</div>
                <div>${Number(r.cost || 0).toFixed(2)}</div>
                <div className="cap">{r.status || "open"}</div>
                <div className="row-actions"><button className="link" onClick={() => markAsComplete(r)}>Mark Complete</button></div>
              </div>
            )) : <div className="row muted">No maintenance records yet.</div>}
          </div>
        </div>
      </section>

      {/* Fuel */}
      <section className="panel">
        <div className="panel-head flex-between">
          <h3>Fuel Expenses</h3>
          <button className="mini" onClick={() => setFuelForm({ date: today() })}>+ Log Fuel</button>
        </div>
        {fuelForm && (
          <form className="fleet-inline-form" onSubmit={submitFuel}>
            <input type="date" value={fuelForm.date} onChange={(e) => setFuelForm({ ...fuelForm, date: e.target.value })} />
            <input type="number" step="0.01" placeholder="Gallons" value={fuelForm.volume || ""} onChange={(e) => setFuelForm({ ...fuelForm, volume: e.target.value })} required />
            <input type="number" step="0.01" placeholder="Total $" value={fuelForm.price_total || ""} onChange={(e) => setFuelForm({ ...fuelForm, price_total: e.target.value })} required />
            <input type="number" placeholder="Odometer" value={fuelForm.odometer || ""} onChange={(e) => setFuelForm({ ...fuelForm, odometer: e.target.value })} />
            <button type="submit">Save</button>
            <button type="button" className="secondary" onClick={() => setFuelForm(null)}>Cancel</button>
          </form>
        )}
        <div className="table">
          <div className="thead"><div>Date</div><div>Gallons</div><div>Total</div><div>Odometer</div></div>
          <div className="tbody">
            {fuelLogs.length > 0 ? fuelLogs.map((f) => (
              <div key={f.id} className="row small">
                <div>{f.date || "—"}</div>
                <div>{f.volume ?? "—"}</div>
                <div>${Number(f.price_total || 0).toFixed(2)}</div>
                <div>{f.odometer?.toLocaleString?.() || "—"}</div>
              </div>
            )) : <div className="row muted">No fuel expenses logged yet.</div>}
          </div>
        </div>
      </section>

      {/* Inspections */}
      <section className="panel">
        <div className="panel-head flex-between">
          <h3>Inspections</h3>
          <button className="mini" onClick={() => setShowInspect(true)}>+ New Inspection</button>
        </div>
        <div className="table">
          <div className="thead"><div>Date</div><div>Type</div><div>Result</div><div>Inspector</div></div>
          <div className="tbody">
            {inspections.length > 0 ? inspections.map((i) => (
              <div key={i.id} className="row small">
                <div>{(i.completed_at || "").slice(0, 10) || "—"}</div>
                <div className="cap">{(i.inspection_type || "").replace(/_/g, " ")}</div>
                <div><span className={`inventory-pill ${i.status === "pass" ? "success" : "warn"}`}>{(i.status || "").replace(/_/g, " ")}</span></div>
                <div>{i.inspector_name || "—"}</div>
              </div>
            )) : <div className="row muted">No inspections yet.</div>}
          </div>
        </div>
      </section>

      {/* Insurance */}
      <section className="panel">
        <div className="panel-head flex-between">
          <h3>Insurance Policies</h3>
          <button className="mini" onClick={() => setShowInsuranceForm(true)}>+ Add Policy</button>
        </div>
        <div className="table">
          <div className="thead"><div>Provider</div><div>Policy #</div><div>Start</div><div>Expiration</div><div>Monthly</div></div>
          <div className="tbody">
            {insurance.length > 0 ? insurance.map((p) => (
              <div key={p.id} className="row small">
                <div>{p.provider || "—"}</div>
                <div>{p.policy_number || "—"}</div>
                <div>{p.start_date || "—"}</div>
                <div>{p.expiration_date || "—"}</div>
                <div>${p.monthly_premium || "—"}</div>
              </div>
            )) : <div className="row muted">No insurance records yet.</div>}
          </div>
        </div>
      </section>

      {/* Documents */}
      <section className="panel">
        <div className="panel-head flex-between">
          <h3>Documents</h3>
          <label className="mini" style={{ cursor: "pointer" }}>
            + Upload
            <input type="file" accept="application/pdf,image/*" style={{ display: "none" }} onChange={onUploadDoc} />
          </label>
        </div>
        <div className="table">
          <div className="thead"><div>Name</div><div>Category</div><div>Added</div><div></div></div>
          <div className="tbody">
            {docs.length > 0 ? docs.map((d) => (
              <div key={d.id || d.file_uuid} className="row small">
                <div>{d.name || "—"}</div>
                <div className="cap">{(d.category || "").replace(/_/g, " ")}</div>
                <div>{(d.created_at || "").slice(0, 10)}</div>
                <div className="row-actions">
                  {d.url && <a className="link" href={d.url} target="_blank" rel="noreferrer">Open</a>}
                </div>
              </div>
            )) : <div className="row muted">No documents. Registration, insurance, and receipts attach here.</div>}
          </div>
        </div>
      </section>

      {showInsuranceForm && (
        <InsuranceFormModal
          vehicleId={vehicleId}
          onSave={async (data) => { await createInsurance(data); await loadAll(); }}
          onClose={() => setShowInsuranceForm(false)}
        />
      )}
      {showInspect && (
        <InspectionModal
          vehicle={vehicle}
          onSubmit={async (payload) => { await submitInspection(payload); await loadAll(); }}
          onClose={() => setShowInspect(false)}
        />
      )}
    </div>
  );
}
